import config from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import type { Lang } from "@/hooks/useLang/types";
import usePopup from "@/hooks/usePopup";
import useStore from "@/hooks/useStore";
import type { TyActiveStore } from "@/hooks/useStore/types";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse } from "@/types";
import LanguagePicker from "@/UI/inputs/LanguagePicker";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import FileInput from "@/UI/inputs/FileInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import SlugInfoPopup from "@/UI/popups/SlugInfoPopup";
import { getMenuHost, hasAnyLangValue, isValidSlug, sanitizeSlugInput, writeLangs } from "@/utils";
import type { TyLangValues } from "@/utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles.css";

/** Stands in for the slug in the live URL preview — stays latin so the preview reads as a URL. */
const SLUG_PREVIEW_FALLBACK = "yourslug";

type TyStoreType = "cafe" | "restaurant" | "both" | "other";

type TyCreateStoreResponse = TyApiResponse<{ store: TyActiveStore }>;

export default function CreateStoreScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const token = useAuth((state) => state.token);
    const client = useAuth((state) => state.client);
    const updateClient = useAuth((state) => state.updateClient);
    const setStore = useStore((state) => state.setStore);
    const { addComp, removeComp } = usePopup();

    /**
     * `null` until the languages are chosen — the screen opens on that question, because every text
     * field below it is sized by the answer. Confirming keeps whatever was already typed, so stepping
     * back to add a language does not cost the form.
     */
    const [languages, setLanguages] = useState<Lang[] | null>(null);
    const [draftLanguages, setDraftLanguages] = useState<Lang[]>([config.menuLanguages[0]]);

    const [logo, setLogo] = useState<File | null>(null);
    const [name, setName] = useState<TyLangValues>({});
    const [slug, setSlug] = useState("");
    const [tagline, setTagline] = useState<TyLangValues>({});
    const [type, setType] = useState<TyStoreType | "">("");

    const { data, loading, error, invoke } = useFetch<TyCreateStoreResponse>("auth/create-store", { method: "POST" }, false);

    useEffect(() => {
        if (!token) navigate("/login", { replace: true });
    }, [token, navigate]);

    useEffect(() => {
        if (!data?.success) return;
        const newStoreId = data.data.store._id;
        setStore(newStoreId, data.data.store);
        if (client) updateClient({ ...client, owner: [...client.owner, newStoreId] });
        navigate("/");
    }, [data]);

    /** "Slug" means nothing to a cafe owner, and the value is hard to change once QR codes are printed. */
    const openSlugInfo = () => {
        const name = "slug-info";
        removeComp(name);
        addComp({ name, Component: SlugInfoPopup, place: "center", anim: "scale", cover: "shaded", details: { variant: "store" } });
    };

    const isSlugValid = isValidSlug(slug);
    // A name in one language is enough, and a tagline is optional — the menu falls back across
    // languages, so demanding every box be filled would only block stores that sell in one.
    const isValid = Boolean(hasAnyLangValue(name) && isSlugValid && type);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid || !languages) return;
        const chosen = languages;
        invoke({
            data: {
                name: writeLangs(name, chosen, chosen[0]),
                slug,
                tagline: writeLangs(tagline, chosen, chosen[0]),
                type,
                languages: chosen,
                ...(logo ? { logo } : {}),
            },
        });
    };

    return (
        <div className="create-store">
            {!languages ? (
                <div className="create-store-card">
                    <div className="create-store-header">
                        <p className="create-store-title">{t.createStore.languagesTitle}</p>
                        <p className="create-store-subtitle">{t.createStore.languagesSubtitle}</p>
                    </div>

                    <LanguagePicker value={draftLanguages} onChange={setDraftLanguages} />

                    <button type="button" className="create-store-submit" onClick={() => setLanguages(draftLanguages)}>
                        {t.createStore.languagesContinue}
                    </button>
                </div>
            ) : (
                <form className="create-store-card" onSubmit={handleSubmit}>
                    <div className="create-store-header">
                        <p className="create-store-title">{t.createStore.title}</p>
                        <p className="create-store-subtitle">{t.createStore.subtitle}</p>
                    </div>

                    <FileInput value={logo} onChange={setLogo} label={t.createStore.logoLabel} disabled={loading} />

                    <MultiLangInput
                        label={t.createStore.nameLabel}
                        languages={languages}
                        values={name}
                        onChange={(lang, value) => setName((prev) => ({ ...prev, [lang]: value }))}
                        disabled={loading}
                    />

                    <div className="create-store-slug-group">
                        <div className="create-store-slug-field">
                            <TextInput
                                value={slug}
                                onChange={(value) => setSlug(sanitizeSlugInput(value))}
                                placeholder={t.createStore.slugPlaceholder}
                                disabled={loading}
                                dir="ltr"
                            />
                            <button type="button" className="create-store-slug-info" onClick={openSlugInfo} aria-label={t.slugInfo.storeTitle}>
                                <img src="/icons/info-circle-icon.svg" alt="" width={18} height={18} />
                            </button>
                        </div>
                        <p className="create-store-slug-hint">
                            {t.createStore.slugHint} <span dir="ltr">{getMenuHost(slug || SLUG_PREVIEW_FALLBACK)}</span>
                        </p>
                        {slug && !isSlugValid && <p className="create-store-slug-error">{t.createStore.slugInvalid}</p>}
                    </div>

                    <MultiLangInput
                        label={t.createStore.taglineLabel}
                        languages={languages}
                        values={tagline}
                        onChange={(lang, value) => setTagline((prev) => ({ ...prev, [lang]: value }))}
                        disabled={loading}
                    />

                    <SelectInput
                        value={type}
                        onChange={(value) => setType(value as TyStoreType)}
                        placeholder={t.createStore.typePlaceholder}
                        disabled={loading}
                        options={[
                            { value: "cafe", label: t.createStore.typeCafe },
                            { value: "restaurant", label: t.createStore.typeRestaurant },
                            { value: "both", label: t.createStore.typeBoth },
                            { value: "other", label: t.createStore.typeOther },
                        ]}
                    />

                    {error && <p className="create-store-error">{error}</p>}

                    <button type="submit" className="create-store-submit" disabled={!isValid || loading}>
                        {t.createStore.submitButton}
                    </button>

                    {/* Stepping back keeps everything already typed — only the language list is re-opened. */}
                    <button type="button" className="create-store-back" onClick={() => setLanguages(null)} disabled={loading}>
                        {t.createStore.languagesBack}
                    </button>
                </form>
            )}
        </div>
    );
}
